// pwa/src/components/Products.js
import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../utilities/context";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Container from "@mui/material/Container";
import Layout from "./Layout";
import Grid from "@mui/material/Grid";
// import DOMPurify from 'dompurify';
import { Button, Box } from "@mui/material";
import Typography from "@mui/material/Typography";

const Products = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [syncState, setSyncState] = useState({
    isLoading: false,
    isSyncing: false,
    progress: 0,
    status: "none",
    error: null,
  });

  const checkSyncStatus = async () => {
    try {
      const response = await axios.get(
        "/api/marketplace/tiendanube/backgroundtask/status"
      );
      const prevTask = response.data;
      const newState = {
        ...syncState,
        isSyncing: ["pending", "processing"].includes(prevTask.status),
        isLoading: ["pending", "processing"].includes(prevTask.status),
        progress: prevTask.progress || 0,
        status: prevTask.status,
        processedItems: prevTask.processedItems,
        totalItems: prevTask.totalItems,
      };

      setSyncState(newState);

      // Si la sincronización terminó, actualizar los productos
      if (!["pending", "processing"].includes(prevTask.status)) {
        getProducts();
      }
    } catch (error) {
      console.error("Error al verificar estado:", error);
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        isLoading: false,
        status: "error",
        error: error.message,
      }));
    }
  };

  const getProducts = () => {
    const { page, pageSize } = paginationModel;

    let sortParams = "";
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortParams = `&sortField=${field}&sortOrder=${sort}`;
    }

    axios
      .get("/api/products?page=" + page + "&pageSize=" + pageSize + sortParams)
      .then((response) => {
        setProducts(response.data.products);
        setTotalRows(response.data.total);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const syncProducts = () => {
    // 1. Resetear el estado y marcar como sincronizando
    setSyncState({
      isLoading: true,
      isSyncing: true,
      progress: 0,
      status: "pending",
      error: null,
      processedItems: 0,
      totalItems: 0,
    });
    axios
      .get("/api/marketplace/tiendanube/syncproducts")
      .then((response) => {
        context.updateAlertState([
          {
            open: true,
            message: "Sincro iniciada",
            severity: "success",
          },
        ]);
      })
      .catch((error) => {
        let errorMessage = error.message;
        if (error.response && error.response.status === 409) {
          errorMessage = intl.formatMessage({
            id: "app.products.sync.conflict",
          });
        }

        setSyncState((prev) => ({
          ...prev,
          isSyncing: false,
          isLoading: false,
          status: "error",
          error: errorMessage,
        }));
        context.updateAlertState([
          {
            open: true,
            message: errorMessage,
            severity: "error",
          },
        ]);
      });
    // 3. Iniciar inmediatamente la verificación (no esperar al siguiente intervalo)
    checkSyncStatus();
  };

  useEffect(() => {
    getProducts();
    checkSyncStatus();
    if (context) {
      context.updateBreadcrumbsState([
        {
          label: intl.formatMessage({ id: "app.header.home.label" }),
          url: "/",
        },
        {
          label: intl.formatMessage({ id: "app.header.shop.products.label" }),
        },
      ]);
    }
  }, [paginationModel, sortModel]); // Se ejecuta cuando cambia la paginación o la ordenación

  useEffect(() => {
    let interval;
    if (syncState.status === "pending" || syncState.status === "processing") {
      // Ejecutar inmediatamente y luego cada 3 segundos
      interval = setInterval(checkSyncStatus, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncState.status]); // Ahora observamos syncState.status en lugar de isSyncing

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 100 },
    { field: "description", headerName: "Description", flex: 1 },
    // {
    //   field: "name",
    //   headerName: "Nombre",
    //   width: 200,
    //   valueGetter: (params) => {
    //     return params?.es || "";
    //   },
    // },
    // {
    //   field: "description",
    //   headerName: "Description",
    //   valueGetter: (params) => {
    //     return params?.es || "";
    //   },
    //   flex: 1, // Esta columna ocupará el espacio disponible
    //   // renderCell: (params) => {
    //   //   console.log("renderCell params:", params);
    //   //   return (<div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(params.value?.es || '')}} />)
    //   // },
    // },
    // { field: "price", headerName: "Precio", width: 150, type: "number" },
  ];

  return (
    <Layout>
      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={5} sx={{ textAlign: "left" }}>
            <Typography
              component="p"
              variant="p"
              align="left"
              color="text.primary"
              gutterBottom
            >
              <FormattedMessage
                id={"app.products.description"}
                defaultMessage={
                  "In the following Data Grid you will see the list of products that are synced with the AI."
                }
              />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              size="small"
              onClick={syncProducts}
              sx={{ mr: 1 }}
              disabled={syncState.isLoading || syncState.isSyncing}
            >
              {syncState.isLoading || syncState.isSyncing
                ? "Working..."
                : "Sync Products"}
            </Button>
          </Grid>

          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10} sx={{ textAlign: "center" }}>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={products}
                columns={columns}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={+totalRows}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default Products;
